import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TYPE } from '@kontentino/kontentino-constants/Pages';
import routes from 'constants/routes';
import PageTypeUtils from 'app/utils/pageType';
import type {
  MentionPartialReason,
  MentionSearchFailure,
  MentionSearchFailureReason,
} from 'api/mentions/types';

type Props =
  | {
      variant: 'empty';
      query: string;
      notice: string | null;
      partialReason: MentionPartialReason | null;
      pageType: number;
    }
  | { variant: 'failure'; failure: MentionSearchFailure; pageType: number }
  | { variant: 'transportError' };

/**
 * Exported so the Instagram location picker renders the same sentence for the
 * same reason. It cannot use this component — react-select's empty state takes
 * a string, not a node — but the copy must not fork.
 */
export const COPY_KEY_BY_REASON: Record<MentionSearchFailureReason, string> = {
  connection_expired: 'mentions.search.connectionExpired',
  permission_missing: 'mentions.search.permissionMissing',
  no_token_for_page: 'mentions.search.noTokenForPage',
  rate_limited: 'mentions.search.rateLimited',
  upstream: 'mentions.search.upstream',
  unknown: 'mentions.search.unknown',
  misconfigured: 'mentions.search.misconfigured',
};

/**
 * Copy for the reasons that can explain an EMPTY result. Partial on purpose:
 * a reason with no entry falls back to the envelope's English `notice`, which
 * is what every partial notice has always rendered. Adding a key here is what
 * upgrades one reason from that fallback to real copy.
 */
const COPY_KEY_BY_PARTIAL_REASON: Partial<
  Record<MentionPartialReason, string>
> = {
  personal_profile_page: 'mentions.search.personalProfilePage',
};

const MentionSearchFailureNotice: FC<Props> = (props) => {
  const { t } = useTranslation();

  const message = (() => {
    switch (props.variant) {
      case 'failure':
        return t(COPY_KEY_BY_REASON[props.failure.reason], {
          // The envelope's own `failure.network` is the raw key the backend
          // maps a page type to -- `linkedin`, `facebook` -- and six of the
          // seven copy strings interpolate it into a sentence, so it reached
          // users as "your linkedin connection has expired". `PageTypeUtils`
          // is the map the rest of the app already labels networks with (it
          // is where Twitter becomes "X" and GMB becomes "Google"), and the
          // composer already holds the page type, so nothing new is invented
          // and no contract changes. The two cannot disagree: both are
          // derived from the same page.
          network: PageTypeUtils.getLabel(props.pageType),
          pageName: props.failure.pageName,
        });
      case 'empty':
        return t('mentions.search.empty', { query: props.query });
      case 'transportError':
        return t('mentions.search.transportError');
    }
  })();

  // Three tiers, most specific first: translated copy for a reason we
  // recognise, then the server's English sentence, then the generic hint.
  const emptyHint = (() => {
    if (props.variant !== 'empty') {
      return null;
    }

    const copyKey =
      props.partialReason === null
        ? undefined
        : COPY_KEY_BY_PARTIAL_REASON[props.partialReason];

    if (copyKey) {
      return t(copyKey);
    }

    if (props.notice) {
      return props.notice;
    }

    // The generic hint sends the user to Direct mention, which is right on
    // Facebook and a DEAD END on LinkedIn: LinkedIn exposes no URL-to-person
    // lookup, so pasting a profile link is refused with "search for them by
    // name instead" -- the advice this hint just gave in reverse. The two
    // strings were written three weeks apart in different files and closed a
    // loop nobody could escape.
    //
    // LinkedIn's real constraint is the one to state: its people typeahead is
    // scoped to the organisation's FOLLOWERS, so someone who does not follow
    // the page cannot be found by any route. Direct mention still earns its
    // mention there, because it does resolve LinkedIn COMPANIES.
    if (props.pageType === TYPE.LINKEDIN) {
      return t('mentions.search.emptyHintLinkedIn');
    }

    return t('mentions.search.emptyHint');
  })();

  const showAuthorizationCta =
    props.variant === 'failure' && props.failure.isConnectionProblem;

  return (
    <div
      className="tw-rounded-lg tw-bg-white tw-p-3 tw-text-md tw-text-grayscale-180 tw-shadow-xl"
      data-cy="mention-suggestions_failure"
      data-name="mention-suggestions_failure"
    >
      <div>{message}</div>
      {props.variant === 'empty' && (
        <div
          className="tw-mt-1 tw-text-sm tw-text-grayscale-100"
          data-cy="mention-suggestions_empty-hint"
          data-name="mention-suggestions_empty-hint"
        >
          {/*
            A server notice REPLACES the generic hint rather than joining it,
            and not merely to keep the box short. The hint sends the user to
            Direct mention to paste a profile link -- which, on the page that
            produces the only notice reaching this branch today, a LinkedIn
            personal profile, is refused outright. Showing both would answer
            "you can't tag people from here" with "so paste their link",
            which is a second dead end.

            Untranslated on purpose: `notice` is server-supplied English, the
            same accepted debt the partial notice above the suggestion list
            already carries. Inventing a key here would mean the same sentence
            translated in one place and not the other.
          */}
          {emptyHint}
        </div>
      )}
      {showAuthorizationCta && (
        <Link
          to={routes.SETTINGS.AUTHORIZATION}
          className="tw-mt-2 tw-inline-block tw-font-medium tw-text-primary-100"
          data-cy="mention-suggestions_failure-cta"
          data-name="mention-suggestions_failure-cta"
        >
          {t('mentions.search.goToAuthorization')}
        </Link>
      )}
    </div>
  );
};

export default MentionSearchFailureNotice;
